// widgets/ProjectCard.jsx

import React, { useCallback } from 'react';
import { Code, ExternalLink } from 'lucide-react';

const ProjectCard = ({ project, darkMode }) => {
    // Determine card-specific theme classes
    const cardClasses = {
        cardBg: darkMode ? 'bg-card-dark shadow-2xl' : 'bg-card-light shadow-xl',
        cardText: darkMode ? 'text-muted-dark' : 'text-muted-light',
        cardTitle: darkMode ? 'text-text-dark' : 'text-text-light',
        badgeBg: darkMode ? 'bg-secondary/20 text-secondary' : 'bg-primary/10 text-primary',
        subText: darkMode ? 'text-info' : 'text-primary',
    };

    const handleCardClick = useCallback(() => {
        if (project.liveLink) {
            window.open(project.liveLink, '_blank');
        } else if (project.codeLink) {
            window.open(project.codeLink, '_blank');
        }
    }, [project.liveLink, project.codeLink]);

    return (
        <div
            className="w-full h-full"
            onClick={handleCardClick}
        >
            <div
                className={`flex flex-col h-full rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl ${cardClasses.cardBg}`}
            >
                {/* Image Container */}
                <div className="h-[180px] overflow-hidden relative" style={{ backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa' }}>
                    {project.image ? (
                        <img
                            src={`/images/projects/${project.image}`}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-300 ease-in-out hover:scale-[1.05]"
                            loading="lazy"
                        />
                    ) : (
                        <div className="flex flex-col justify-center items-center h-full">
                            <Code size={48} className={darkMode ? 'text-secondary' : 'text-primary'} />
                            <p className={`mt-2 text-sm ${cardClasses.cardText}`}>Project Snapshot Unavailable</p>
                        </div>
                    )}
                </div>

                {/* Card Body */}
                <div className="p-5 flex flex-col flex-grow">
                    
                    <h5 className={`text-lg font-semibold mb-3 ${cardClasses.cardTitle} line-clamp-2`}>
                        {project.title}
                    </h5>

                    {/* Description */}
                    <p className={`text-sm mb-3 ${cardClasses.cardText} line-clamp-3 flex-grow`}>
                        {project.description}
                    </p>
                    
                    {/* Meta Data */}
                    <div className="space-y-1 mb-3 text-xs">
                        {project.ai_tools_used && project.ai_tools_used.length > 0 && (
                            <div className="flex items-center">
                                <span className={`mr-2 font-medium ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Tools:</span>
                                <span className={cardClasses.subText}>
                                    {project.ai_tools_used.join(', ')}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center">
                            <span className={`mr-2 font-medium ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Duration:</span>
                            <span className={cardClasses.subText}>
                                {project.development_time}
                            </span>
                        </div>
                    </div>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {project.technologies.map((tech, techIndex) => (
                            <span
                                key={techIndex}
                                className={`px-2 py-0.5 text-xs font-medium rounded-full ${cardClasses.badgeBg}`}
                            >
                                {tech}
                            </span>
                        ))}
                    </div>

                    {/* Action Button */}
                    <button
                        className={`mt-auto w-full flex items-center justify-center space-x-2 py-2 px-4 text-sm font-semibold rounded-lg transition duration-300 
                            ${project.liveLink 
                                ? 'bg-primary dark:bg-secondary text-white hover:bg-primary/90 dark:hover:bg-secondary/90' 
                                : 'border border-primary dark:border-secondary text-primary dark:text-secondary hover:bg-primary/10 dark:hover:bg-secondary/10'
                            }`}
                        onClick={e => {
                            e.stopPropagation();
                            window.open(project.liveLink || project.codeLink, '_blank');
                        }}
                        disabled={!project.liveLink && !project.codeLink}
                    >
                        {project.liveLink ? (
                            <>
                                <ExternalLink size={16} /> <span>View Live</span>
                            </>
                        ) : (
                            <>
                                <Code size={16} /> <span>View Code</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(ProjectCard);